import { Link } from "react-router-dom";
import { Facebook, Instagram } from "lucide-react";
import { ASSETS, BRAND, NAV } from "@/lib/brand";

export default function Footer() {
  const year = new Date().getFullYear();
  const go = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer
      data-testid="footer"
      className="relative bg-[#0A0A0A] text-[#F2EFE8]"
    >      <div className="max-w-[1480px] mx-auto px-6 sm:px-12 lg:px-20 py-20 sm:py-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <img
                src={ASSETS.logo}
                alt="Stemma La Valle degli Iblei"
                className="h-12 w-12 object-contain"
              />
              <span className="font-serif-display text-2xl">
                La Valle degli Iblei
              </span>
            </div>
            <p className="font-serif-display italic text-2xl sm:text-3xl leading-snug max-w-md text-[#E7E2D5]">
              "Trasformiamo la siccità in eleganza, il mito in calice."
            </p>
            <p className="mt-6 text-sm font-light text-[#A39E92] max-w-md leading-relaxed">
              Selezione d'Autore di {BRAND.founder}. Micro-vinificazioni e lotti
              d'eccellenza dalla Sicilia sud-orientale.
            </p>
          </div>

          <div className="md:col-span-3">
            <span className="overline text-[10px] text-[#C4A671] block mb-5">
              Navigazione
            </span>
            <ul className="space-y-3">
              {NAV.map((n) => (
                <li key={n.id}>
                  <button
                    data-testid={`footer-nav-${n.id}`}
                    onClick={() => go(n.id)}
                    className="font-serif-display italic text-lg hover:text-[#C4A671] transition"
                  >
                    {n.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-4">
            <span className="overline text-[10px] text-[#C4A671] block mb-5">
              Contatti Commerciali
            </span>
            <a
              href={`mailto:${BRAND.email}`}
              data-testid="footer-email"
              className="font-serif-display text-xl hover:text-[#C4A671] transition block break-all"
            >
              {BRAND.email}
            </a>
            <a
              href="https://www.lavalledegliiblei.it"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-website"
              className="font-serif-display italic text-base text-[#C4A671] hover:text-white transition block mt-2"
            >
              www.lavalledegliiblei.it
            </a>
            <p className="mt-4 text-sm font-light text-[#A39E92]">
              {BRAND.location}
            </p>

            <div className="mt-6 flex items-center gap-4" data-testid="footer-socials">
              <a
                href="https://www.instagram.com/lavalledegliiblei"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram — La Valle degli Iblei"
                data-testid="footer-social-instagram"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-[#2a2a2a] text-[#A39E92] hover:text-[#C4A671] hover:border-[#C4A671] transition-colors duration-300"
              >
                <Instagram size={16} strokeWidth={1.5} />
              </a>
              <a
                href="https://www.facebook.com/61591722311481"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook — La Valle degli Iblei"
                data-testid="footer-social-facebook"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-[#2a2a2a] text-[#A39E92] hover:text-[#C4A671] hover:border-[#C4A671] transition-colors duration-300"
              >
                <Facebook size={16} strokeWidth={1.5} />
              </a>
            </div>

            <button
              data-testid="footer-cta"
              onClick={() => go("contatti")}
              className="mt-8 inline-flex items-center gap-3 text-[10.5px] font-medium tracking-[0.28em] uppercase border-b border-[#C4A671] text-[#C4A671] pb-2 hover:text-white hover:border-white transition"
            >
              Richiedi Allocazioni →
            </button>
          </div>
        </div>

        {/* Legal Compliance Block — Brand Development Disclaimer */}
        <div className="mt-20 pt-10 border-t border-[#1f1f1f]" data-testid="footer-legal-block">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12">
            <div className="md:col-span-3">
              <span className="overline text-[10px] text-[#C4A671] block">
                Nota Legale
              </span>
              <span className="font-serif-display italic text-base text-[#E7E2D5] mt-2 block">
                Conformità &amp; Tutela
              </span>
            </div>
            <div className="md:col-span-9">
              <p
                className="text-[12.5px] leading-[1.85] text-[#A39E92] font-light max-w-3xl"
                data-testid="footer-legal-disclaimer"
              >
                <span className="text-[#C4A671] font-serif-display italic">
                  La Valle degli Iblei
                </span>{" "}
                è un progetto di Brand Development, Marketing e Direzione
                Commerciale di {BRAND.founder}. Le attività fisiche di
                produzione, vinificazione, affinamento e imbottigliamento sono
                interamente delegate e realizzate presso cantine partner
                vinicole terze, autorizzate e in possesso di regolari licenze
                di legge, certificazioni DOC/IGT e abilitazioni sanitarie
                conformi alla normativa vigente nazionale ed europea in materia
                enologica. Il marchio opera secondo un modello operativo{" "}
                <em className="font-serif-display text-[#E7E2D5]">
                  &quot;asset-light&quot;
                </em>{" "}
                di selezione, curatela e valorizzazione commerciale delle
                referenze.
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl">
                <FiscalItem label="Proprietà del Marchio e Direzione" value="Claudio Pucci" />
                <FiscalItem label="Sede Operativa" value="Sicilia, Italia" />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-[#1f1f1f] flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-xs text-[#7A7568] font-light">
            © {year} La Valle degli Iblei — Selezione d'Autore di {BRAND.founder}. {BRAND.location}.
          </p>
          <div className="flex flex-wrap items-center gap-6">
            <Link
              to="/privacy"
              data-testid="footer-privacy"
              className="overline text-[9px] text-[#7A7568] hover:text-[#C4A671] transition"
            >
              Privacy Policy
            </Link>
            <Link
              to="/cookie-policy"
              data-testid="footer-cookie-policy"
              className="overline text-[9px] text-[#7A7568] hover:text-[#C4A671] transition"
            >
              Cookie Policy
            </Link>
            <p className="overline text-[9px] text-[#7A7568]">
              Bere responsabilmente · 18+
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FiscalItem({ label, value }) {
  return (
    <div className="border-l border-[#1f1f1f] pl-4">
      <span className="overline text-[9px] text-[#C4A671] block">
        {label}
      </span>
      <span className="text-[12px] text-[#A39E92] font-light mt-1 block leading-snug">
        {value}
      </span>
    </div>
  );
}
